import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';

// Enhanced UI Components
const Spinner = () => (
  <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
);

const ProgressBar = ({ progress }: { progress: number }) => (
  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
    <div
      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
      style={{ width: `${progress}%` }}
    ></div>
  </div>
);

const SuccessAnimation = ({ children, onComplete }: { children: React.ReactNode, onComplete?: () => void }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(true);
    const timer = setTimeout(() => {
      onComplete?.();
    }, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className={`success-animation ${show ? 'animate-bounce' : ''}`}>
      <div className="flex flex-col items-center space-y-4">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        {children}
      </div>
    </div>
  );
};

const PhoneAuthPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();

  // Enhanced state management
  const [authMode, setAuthMode] = useState<'password' | 'otp'>('otp'); // Default to OTP
  const [authType, setAuthType] = useState<'login' | 'register'>('register');
  const [currentStep, setCurrentStep] = useState<'form' | 'verify' | 'success'>('form');

  const [formData, setFormData] = useState({
    phone: '+267',
    fullName: '',
    password: '',
    confirmPassword: '',
    otp: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingStates, setLoadingStates] = useState({
    sendingOtp: false,
    verifyingOtp: false,
    registering: false,
    loggingIn: false
  });

  const [progress, setProgress] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [rateLimitUntil, setRateLimitUntil] = useState<Date | null>(null);

  // Refs for auto-focus
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus management
  useEffect(() => {
    if (currentStep === 'form' && phoneInputRef.current) {
      phoneInputRef.current.focus();
    } else if (currentStep === 'verify' && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [currentStep]);

  // Rate limiting countdown
  useEffect(() => {
    if (rateLimitUntil) {
      const interval = setInterval(() => {
        const now = new Date();
        if (now >= rateLimitUntil) {
          setRateLimitUntil(null);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [rateLimitUntil]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number (e.g., +1234567890)';
    }

    if (authMode === 'password') {
      if (!formData.password.trim()) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      if (authType === 'register') {
        if (!formData.fullName.trim()) {
          newErrors.fullName = 'Full name is required';
        } else if (formData.fullName.trim().length < 2) {
          newErrors.fullName = 'Full name must be at least 2 characters';
        }

        if (!formData.confirmPassword.trim()) {
          newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
      }
    } else if (authMode === 'otp' && currentStep === 'verify') {
      if (!formData.otp.trim()) {
        newErrors.otp = 'Verification code is required';
      } else if (formData.otp.length !== 6 || !/^\d{6}$/.test(formData.otp)) {
        newErrors.otp = 'Please enter a valid 6-digit code';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!formData.phone.trim() || !/^\+?[1-9]\d{1,14}$/.test(formData.phone)) {
      setErrors({ phone: 'Please enter a valid phone number' });
      return;
    }

    // Check rate limiting
    if (rateLimitUntil && new Date() < rateLimitUntil) {
      const timeLeft = Math.ceil((rateLimitUntil.getTime() - new Date().getTime()) / 1000);
      toast.error(`Too many attempts. Try again in ${timeLeft} seconds.`);
      return;
    }

    setLoadingStates(prev => ({ ...prev, sendingOtp: true }));
    setErrors({});

    try {
      let response;

      if (authType === 'register') {
        response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/phone-register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: formData.phone, fullName: formData.fullName })
        });
      } else {
        response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/phone-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: formData.phone })
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send verification code');
      }

      const data = await response.json();

      // Check if user already exists
      if (data.alreadyRegistered) {
        toast.error('This phone number is already registered. Please login instead.');
        setAuthType('login');
        return;
      }

      // Show verification code for development
      if (data.verificationCode) {
        toast.success(`Verification code: ${data.verificationCode}`);
      } else {
        toast.success('Verification code sent to your phone');
      }

      setCurrentStep('verify');
      setRetryCount(0);
    } catch (error: any) {
      console.error('OTP send error:', error);
      setRetryCount(prev => prev + 1);

      // Set rate limiting after multiple failures
      if (retryCount >= 3) {
        setRateLimitUntil(new Date(Date.now() + 60000)); // 1 minute
      }

      toast.error(error.message || 'Failed to send verification code');
    } finally {
      setLoadingStates(prev => ({ ...prev, sendingOtp: false }));
    }
  };

  const handleVerifyOtp = async () => {
    if (!validateForm()) return;

    setLoadingStates(prev => ({ ...prev, verifyingOtp: true }));

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/verify-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: formData.phone,
          token: formData.otp,
          type: authType === 'register' ? 'signup' : 'sms'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Verification failed');
      }

      const data = await response.json();
      toast.success('Phone verified successfully!');

      // Show success animation
      setCurrentStep('success');

      // Redirect after animation
      setTimeout(() => {
        const isDashApp = window.location.pathname.startsWith('/dash/');
        if (isDashApp) {
          navigate('/errands');
        } else {
          const urlParams = new URLSearchParams(window.location.search);
          const intendedDestination = urlParams.get('redirect') || '/driver/dashboard';
          navigate(intendedDestination);
        }
      }, 2500);

    } catch (error: any) {
      console.error('OTP verification error:', error);
      toast.error(error.message || 'Verification failed');
    } finally {
      setLoadingStates(prev => ({ ...prev, verifyingOtp: false }));
    }
  };

  // Removed password auth handler - phone auth should be OTP-only for security and simplicity

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-submit OTP when complete
    if (name === 'otp' && value.length === 6 && /^\d{6}$/.test(value)) {
      setTimeout(() => handleVerifyOtp(), 500);
    }
  };

  const resetForm = () => {
    setAuthType('register');
    setAuthMode('otp'); // Default to OTP
    setCurrentStep('form');
    setFormData({
      phone: '+267',
      fullName: '',
      password: '',
      confirmPassword: '',
      otp: ''
    });
    setErrors({});
    setLoadingStates({
      sendingOtp: false,
      verifyingOtp: false,
      registering: false,
      loggingIn: false
    });
    setProgress(0);
    setRetryCount(0);
    setRateLimitUntil(null);
  };

  // URL parameter handling
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get('mode');
    const method = searchParams.get('method');

    if (mode === 'login') setAuthType('login');
    if (method === 'otp') setAuthMode('otp');
  }, [location.search]);

  // Success step
  if (currentStep === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <SuccessAnimation>
            <h3 className="text-xl font-semibold text-gray-900">Phone Verified!</h3>
            <p className="text-gray-600 text-center">Welcome to StashIt</p>
            <p className="text-sm text-gray-500 text-center">Redirecting you now...</p>
          </SuccessAnimation>
        </div>
      </div>
    );
  }

  // OTP verification step
  if (currentStep === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">Verify your phone</h2>
            <p className="mt-2 text-sm text-gray-600">
              Enter the 6-digit code sent to <span className="font-medium">{formData.phone}</span>
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }}>
            <div className="space-y-4">
              <div className="flex justify-center">
                <input
                  ref={otpInputRef}
                  type="text"
                  name="otp"
                  required
                  className="text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-lg px-4 py-3 w-48 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="000000"
                  value={formData.otp}
                  onChange={handleInputChange}
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
              {errors.otp && (
                <p className="text-sm text-red-600 text-center">{errors.otp}</p>
              )}
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={loadingStates.verifyingOtp}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingStates.verifyingOtp ? (
                  <div className="flex items-center space-x-2">
                    <Spinner />
                    <span>Verifying...</span>
                  </div>
                ) : (
                  'Verify Code'
                )}
              </button>

              {loadingStates.verifyingOtp && (
                <ProgressBar progress={progress} />
              )}
            </div>

            <div className="text-center space-y-3">
              <button
                type="button"
                onClick={() => { setCurrentStep('form'); setFormData(prev => ({ ...prev, otp: '' })); }}
                className="text-sm text-blue-600 hover:text-blue-500 font-medium"
              >
                Use different phone number
              </button>

              <div>
                <p className="text-sm text-gray-600">Didn't receive the code?</p>
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loadingStates.sendingOtp || !!rateLimitUntil}
                  className="text-sm text-blue-600 hover:text-blue-500 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {loadingStates.sendingOtp ? 'Sending...' : 'Send again'}
                </button>
              </div>

              {rateLimitUntil && (
                <div className="text-sm text-red-600">
                  Too many attempts. Try again in {Math.ceil((rateLimitUntil.getTime() - Date.now()) / 1000)} seconds.
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">
            {authType === 'register' ? 'Create Account' : 'Welcome Back'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Choose your preferred authentication method
          </p>
        </div>

        {/* Auth Type Selection */}
        <div className="flex justify-center space-x-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setAuthType('register')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              authType === 'register'
                ? 'bg-green-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Sign Up
          </button>
          <button
            onClick={() => setAuthType('login')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              authType === 'login'
                ? 'bg-green-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Sign In
          </button>
        </div>

        {/* Phone Authentication Form (OTP-only) */}
        <div className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                ref={phoneInputRef}
                type="tel"
                name="phone"
                required
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                  errors.phone ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                placeholder="+1234567890"
                value={formData.phone}
                onChange={handleInputChange}
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {authType === 'register' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  required
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.fullName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                />
                {errors.fullName && (
                  <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                )}
              </div>
            )}
          </div>

          <button
            onClick={handleSendOtp}
            disabled={loadingStates.sendingOtp || !!rateLimitUntil}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loadingStates.sendingOtp ? (
              <div className="flex items-center space-x-2">
                <Spinner />
                <span>Sending code...</span>
              </div>
            ) : (
              'Send Verification Code'
            )}
          </button>

          {loadingStates.sendingOtp && (
            <ProgressBar progress={progress} />
          )}

          {rateLimitUntil && (
            <div className="text-sm text-red-600 text-center bg-red-50 p-3 rounded-md">
              Too many attempts. Try again in {Math.ceil((rateLimitUntil.getTime() - Date.now()) / 1000)} seconds.
            </div>
          )}
        </div>

        {/* Footer Links */}
        <div className="text-center space-y-2 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Prefer email authentication?{' '}
            <Link
              to={authType === 'register' ? '/auth/signup' : '/auth/login'}
              className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
            >
              {authType === 'register' ? 'Sign up with email' : 'Sign in with email'}
            </Link>
          </p>

          <p className="text-sm text-gray-600">
            {authType === 'register' ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={() => setAuthType(authType === 'register' ? 'login' : 'register')}
              className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
            >
              {authType === 'register' ? 'Sign in' : 'Sign up'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PhoneAuthPage;
